import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { FiCheckCircle, FiXCircle, FiUsers, FiPlus, FiTrash2, FiKey, FiLayers, FiBox } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const AdminDashboard = () => {
  const { users, presets, joinRequests, verifyContribution, approveJoinRequest, rejectJoinRequest, addPreset, deletePreset, addProjectToPreset, deleteProjectFromPreset, user } = useApp();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('requests');
  const [selectedPresetId, setSelectedPresetId] = useState(null);
  
  const [newPreset, setNewPreset] = useState({ label: '', unit: '', color: 'bg-blue-400', glow: 'shadow-blue-500/50' });
  const [newProject, setNewProject] = useState({ name: '', value: '', description: '' });

  React.useEffect(() => {
    if (!user || user.type !== 'admin') navigate('/');
    if (presets.length > 0 && !selectedPresetId) setSelectedPresetId(presets[0].id);
  }, [user, navigate, presets, selectedPresetId]);

  const pendingContributions = users.flatMap(u => u.contributions
    .filter(c => c.status === 'pending')
    .map(c => ({ ...c, userName: u.name, userId: u.id }))
  );

  const selectedPreset = presets.find(p => p.id === selectedPresetId);

  return (
    <div className="min-h-screen pt-24 px-4 pb-20 galaxy-bg">
      <div className="max-w-6xl mx-auto">
        <header className="mb-10">
          <h1 className="text-4xl font-bold text-white mb-2">Mission Control</h1>
          <p className="text-slate-400">Manage the ecosystem, verify impact, and define project parameters.</p>
        </header>

        <div className="flex space-x-1 bg-slate-900/50 p-1 rounded-xl border border-slate-800 mb-8 w-fit">
          {[
            { id: 'requests', label: 'Join Requests', icon: FiUsers, count: joinRequests.length },
            { id: 'verify', label: 'Verify Impact', icon: FiCheckCircle, count: pendingContributions.length },
            { id: 'presets', label: 'Impact Structure', icon: FiLayers, count: presets.length },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center space-x-2 px-6 py-2.5 rounded-lg transition-all ${activeTab === tab.id ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-slate-400 hover:text-white'}`}>
              <tab.icon size={18} />
              <span>{tab.label}</span>
              {tab.count > 0 && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold ${activeTab === tab.id ? 'bg-white text-blue-600' : 'bg-slate-800 text-slate-400'}`}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden backdrop-blur-md">
          <AnimatePresence mode="wait">
            {activeTab === 'requests' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-6">
                <div className="space-y-4">
                  {joinRequests.length === 0 && <p className="text-center py-10 text-slate-500">No pending join requests.</p>}
                  {joinRequests.map(req => (
                    <JoinRequestCard key={req.id} request={req} onApprove={approveJoinRequest} onReject={rejectJoinRequest} />
                  ))}
                </div>
              </motion.div>
            )}

            {activeTab === 'verify' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                {pendingContributions.length === 0 && <p className="text-center py-20 text-slate-500">All impact signals are verified.</p>}
                <div className="divide-y divide-slate-800">
                  {pendingContributions.map(item => (
                    <div key={item.id} className="p-6 flex items-center justify-between hover:bg-slate-800/20">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-bold text-white">{item.userName}</span>
                          <span className="text-xs text-blue-400 uppercase tracking-widest">{presets.find(p => p.id === item.presetId)?.label}</span>
                        </div>
                        <p className="text-slate-400 text-sm">{item.description}</p>
                        <p className="text-xs text-slate-500 mt-1">Value: {item.amount} {presets.find(p => p.id === item.presetId)?.unit}</p>
                      </div>
                      <button onClick={() => verifyContribution(item.userId, item.id)} className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                        <FiCheckCircle size={16} /> Verify
                      </button>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {activeTab === 'presets' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Category Management */}
                  <div className="space-y-6">
                    <div className="p-6 bg-slate-800/30 border border-slate-700 rounded-xl">
                      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><FiPlus /> New Category</h3>
                      <div className="space-y-4">
                        <input placeholder="Label (e.g. Trees Planted)" className="w-full bg-slate-900 border border-slate-700 p-2.5 rounded-lg text-white" value={newPreset.label} onChange={e => setNewPreset({ ...newPreset, label: e.target.value })} />
                        <input placeholder="Unit (e.g. Count)" className="w-full bg-slate-900 border border-slate-700 p-2.5 rounded-lg text-white" value={newPreset.unit} onChange={e => setNewPreset({ ...newPreset, unit: e.target.value })} />
                        <button onClick={() => { addPreset(newPreset); setNewPreset({ label: '', unit: '', color: 'bg-blue-400', glow: 'shadow-blue-500/50' }); }} className="w-full bg-blue-600 py-2.5 rounded-lg font-bold text-white hover:bg-blue-500">Create Category</button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <h3 className="text-sm font-bold text-slate-500 uppercase px-2">Categories</h3>
                      {presets.map(p => (
                        <button key={p.id} onClick={() => setSelectedPresetId(p.id)} className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${selectedPresetId === p.id ? 'bg-blue-600/10 border-blue-500 text-white' : 'bg-slate-800/40 border-slate-700 text-slate-400 hover:border-slate-600'}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${p.color}`} />
                            <span className="font-medium">{p.label}</span>
                          </div>
                          <FiLayers size={14} />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Project Management */}
                  <div className="lg:col-span-2 space-y-6">
                    {selectedPreset ? (
                      <>
                        <div className="flex justify-between items-center">
                          <h3 className="text-2xl font-bold text-white">Projects for {selectedPreset.label}</h3>
                          <button onClick={() => deletePreset(selectedPreset.id)} className="text-red-400 hover:bg-red-400/10 px-3 py-1 rounded-lg text-xs flex items-center gap-1"><FiTrash2 /> Delete Category</button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="p-6 bg-slate-800/30 border border-slate-700 rounded-xl h-fit">
                            <h4 className="font-bold text-white mb-4 flex items-center gap-2 text-sm"><FiBox className="text-blue-400" /> Define New Project</h4>
                            <div className="space-y-4">
                              <input placeholder="Project Name" className="w-full bg-slate-900 border border-slate-700 p-2.5 rounded-lg text-white text-sm" value={newProject.name} onChange={e => setNewProject({ ...newProject, name: e.target.value })} />
                              <div className="flex items-center gap-2">
                                <input type="number" placeholder="Fixed Value" className="flex-1 bg-slate-900 border border-slate-700 p-2.5 rounded-lg text-white text-sm" value={newProject.value} onChange={e => setNewProject({ ...newProject, value: e.target.value })} />
                                <span className="text-slate-500 text-xs">{selectedPreset.unit}</span>
                              </div>
                              <textarea placeholder="Predefined Description" className="w-full bg-slate-900 border border-slate-700 p-2.5 rounded-lg text-white text-sm h-24 resize-none" value={newProject.description} onChange={e => setNewProject({ ...newProject, description: e.target.value })} />
                              <button onClick={() => { addProjectToPreset(selectedPresetId, newProject); setNewProject({ name: '', value: '', description: '' }); }} className="w-full bg-blue-600 py-2.5 rounded-lg font-bold text-white hover:bg-blue-500 text-sm">Add Project</button>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Active Projects</h4>
                            {selectedPreset.projects.length === 0 && <p className="text-slate-500 text-sm italic">No projects defined yet.</p>}
                            {selectedPreset.projects.map(proj => (
                              <div key={proj.id} className="p-4 bg-slate-800/40 border border-slate-700 rounded-xl group relative">
                                <button onClick={() => deleteProjectFromPreset(selectedPreset.id, proj.id)} className="absolute top-2 right-2 text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><FiTrash2 size={14} /></button>
                                <div className="text-white font-bold mb-1">{proj.name}</div>
                                <p className="text-slate-400 text-xs mb-2 line-clamp-2">{proj.description}</p>
                                <div className="text-[10px] font-bold text-blue-400 uppercase tracking-widest">Value: {proj.value} {selectedPreset.unit}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="h-64 flex items-center justify-center border-2 border-dashed border-slate-800 rounded-2xl text-slate-600">Select a category to manage projects</div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

const JoinRequestCard = ({ request, onApprove, onReject }) => {
  const [isApproving, setIsApproving] = useState(false);
  const [creds, setCreds] = useState({ user: '', pass: '' });

  const handleApprove = () => {
    if (!creds.user || !creds.pass) return;
    onApprove(request.id, creds.user, creds.pass);
  };

  return (
    <div className="p-6 bg-slate-800/40 border border-slate-700 rounded-xl">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="text-xl font-bold text-white">{request.name}</h4>
          <p className="text-slate-400 text-sm">Entity: {request.type}</p>
          <p className="text-slate-500 text-xs mt-1">Contact: {request.email}</p>
        </div>
        {!isApproving && (
          <div className="flex gap-2">
            <button onClick={() => setIsApproving(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold">Approve</button>
            <button onClick={() => onReject(request.id)} className="p-2 text-slate-400 hover:text-red-400"><FiXCircle size={20} /></button>
          </div>
        )}
      </div>
      {isApproving && (
        <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} className="pt-4 border-t border-slate-700 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase text-slate-500 font-bold mb-1 block">Username</label>
              <input className="w-full bg-slate-900 border border-slate-700 p-2 rounded text-white text-sm" value={creds.user} onChange={e => setCreds({ ...creds, user: e.target.value })} />
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-500 font-bold mb-1 block">Password</label>
              <input className="w-full bg-slate-900 border border-slate-700 p-2 rounded text-white text-sm" value={creds.pass} onChange={e => setCreds({ ...creds, pass: e.target.value })} />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleApprove} className="flex-1 bg-green-600 py-2 rounded font-bold text-white text-sm flex items-center justify-center gap-2"><FiKey size={14} /> Generate & Invite</button>
            <button onClick={() => setIsApproving(false)} className="px-4 text-slate-400 text-sm">Cancel</button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AdminDashboard;